import {
    fetchIsOpenProjectCampusEnabled,
    fetchIsZammadCampusEnabled,
    HIDABLE_WIDGET_TYPES,
    HidableWidgetType,
    isWidgetCampusEnabled
} from 'features/Rooms/PersonalDashboard/widgetVisibility';
import { useEffect, useState } from 'react';
import { useCurrentOrgBranding, useEnabledWidgetProviders } from 'shared-state/directory/hooks';
import { isMatrixEnabled } from 'kits/matrixFeatureFlag';

export interface IDashboardWidgetsProps {
    draftHiddenWidgets: string[];
    onToggleWidget: (type: HidableWidgetType, hidden: boolean) => void;
    // While a save PATCH is in flight, rows (and the Save button) are disabled so a toggle
    // can't race the round-trip and get clobbered when the server's response is adopted — see
    // IdentityPanel.state.tsx.
    isSaving: boolean;
    // True once the draft differs from the last-saved value — shows the inline Save button
    // below the widget list. Owned by IdentityPanel.state.tsx, not this component, so the draft
    // survives switching to the Profile tab and back.
    isDirty: boolean;
    onSave: () => Promise<void>;
}

export interface IDashboardWidgetsState extends IDashboardWidgetsProps {
    visibleRows: HidableWidgetType[];
    areProvidersLoaded: boolean;
    // Drives the "matrix is always shown and can't be hidden" note. Matrix is disabled outside beta,
    // where that note would advertise a widget the user cannot actually see.
    isMatrixWidgetEnabled: boolean;
}

export const useDashboardWidgetsState: (props: IDashboardWidgetsProps) => IDashboardWidgetsState = (props) => {
    const [enabledProviders, setEnabledProviders] = useEnabledWidgetProviders();
    const [branding] = useCurrentOrgBranding();
    const [isZammadEnabled, setIsZammadEnabled] = useState<boolean | null>(null);
    const [isOpenProjectEnabled, setIsOpenProjectEnabled] = useState<boolean | null>(null);

    useEffect(() => {
        fetchIsZammadCampusEnabled().then(setIsZammadEnabled);
        fetchIsOpenProjectCampusEnabled().then(setIsOpenProjectEnabled);
    }, []);

    // aEnabledWidgetProviders is only ever written by PersonalDashboard, which is lazy-loaded and
    // mounts only once the user's desk renders. Opening My Settings without having visited the desk
    // this session would otherwise leave it null forever, so populate it here from org branding —
    // the same fallback PersonalDashboard uses, and the one that works for non-managers (its other
    // source, idpService.showOrg(), requires the manager role).
    useEffect(() => {
        if (enabledProviders !== null || branding === undefined) return;
        setEnabledProviders(branding.dashboard_widget_providers ?? []);
    }, [branding, enabledProviders]);

    // null means "not known yet", which is distinct from "nothing enabled" — collapsing the two
    // would render the whole tab with zero rows. Callers must wait rather than assume empty.
    const areProvidersLoaded = enabledProviders !== null;

    // A campus-disabled widget gets no row at all (rather than a disabled/greyed row).
    // Order follows HIDABLE_WIDGET_TYPES: Outlook, Calendar, Tasks, Zammad, OpenProject.
    const visibleRows = areProvidersLoaded
        ? HIDABLE_WIDGET_TYPES.filter((type) => {
              if (type === 'zammad') return isZammadEnabled === true;
              if (type === 'openproject') return isOpenProjectEnabled === true;
              return isWidgetCampusEnabled(type, enabledProviders);
          })
        : [];

    return { ...props, visibleRows, areProvidersLoaded, isMatrixWidgetEnabled: isMatrixEnabled() };
};
